import { newAttemptId } from "./attempt-id";
import { fallbackBotCheckConfig, loadBotCheckConfig } from "./bot-check-config";
import { reportBotEvent } from "./bot-events";
import { ensureTurnstileScript, TurnstileScriptError } from "./turnstile-script";

import type { BotCheckConfig } from "./bot-check-config";
import type { BotEvent } from "./bot-events";
import type { TurnstileApi, TurnstileRenderOptions } from "./turnstile-script";
import type { AuthSurface, TurnstileTier } from "./types";

/**
 * Cloudflare Turnstile en dos niveles para los formularios que piden un código por WhatsApp.
 *
 * 1. INVISIBLE (se monta al abrir el formulario). La enorme mayoría pasa acá sin enterarse.
 * 2. CON CLIC (`managed`, `appearance: interaction-only`). Se monta SÓLO si el invisible no dio
 *    token a tiempo, dio error, o la API lo pidió (`TURNSTILE_STEP_UP`). Cloudflare decide si
 *    hace falta el clic; si no hace falta, no se ve nada.
 *
 * Por qué dos niveles: en modo invisible, cuando Cloudflare no puede dar fe de alguien en silencio
 * (redes chicas detrás de CGNAT, por ejemplo) simplemente no emite token y no hay nada que mostrar.
 * Era un callejón sin salida: "Todavía estamos comprobando que seas una persona" en loop. El nivel
 * con clic es la salida.
 *
 * Reglas que sostiene este archivo (y que prueban sus tests):
 * - `getProof()` espera el token invisible hasta `graceMs` contados desde que se pidió el widget
 *   (no desde el clic): quien tardó en completar el formulario no espera nada.
 * - Un error del invisible libera la espera en el acto: no hay por qué esperar el resto del plazo.
 * - Se escala como mucho una vez y no se vuelve atrás: el resto de la página usa el nivel con clic.
 * - El nivel con clic se monta recién cuando se lo necesita (al enviar), nunca de antemano: así la
 *   casilla, si aparece, aparece como respuesta a algo que hizo la persona.
 * - Dos errores (o dos vencimientos) del nivel con clic cortan la espera con un motivo, para que el
 *   formulario muestre un mensaje claro y un botón para reintentar en vez de colgarse.
 * - Lo que pasa mientras se pide un código se reporta por beacon (`/api/auth/bot-event`), aunque
 *   el pedido nunca llegue a salir (escaló, la casilla falló, la persona se fue esperando). Los
 *   errores del widget de ANTES de pedir el código esperan al pedido: abrir un formulario y irse
 *   sin pedir nada no deja registro (es lo que dice la política de privacidad).
 * - Sin formulario no hay pedido: si la persona se fue mientras se verificaba, `getProof()` falla
 *   (`unmounted`) y no sale un send-otp sin dueño.
 */

export type TurnstileProof = {
  /** Sin token cuando el antibot está apagado o cuando no se pudo conseguir. */
  token?: string;
  tier: TurnstileTier;
  /** Por qué no hay token (código de Cloudflare o motivo propio). Va en `clientSignals`. */
  clientError?: string;
  /** Cuánto se esperó en este `getProof()`. Va en `clientSignals.waitedMs`. */
  waitedMs: number;
  /**
   * No hay constancia y no tiene sentido mandar el pedido (la API lo rechazaría): el nivel con
   * clic no pudo dar un token (el slot ya muestra el motivo y cómo reintentar), o el formulario
   * ya no está (`clientError: "unmounted"`: la persona se fue mientras se verificaba).
   */
  failed?: boolean;
};

export type ManagedInteraction = "prompt" | "done" | null;

export type TurnstileSnapshot = {
  /** Hay antibot: la config dice que está prendido (o la API pidió el nivel con clic). */
  enabled: boolean;
  tier: TurnstileTier;
  /** Un `getProof()` está esperando un token: el botón dice "Verificando…". */
  verifying: boolean;
  /**
   * El nivel con clic está mostrando la casilla (`prompt`) o la persona ya la tocó y Cloudflare
   * está terminando (`done`). null: no hay nada para mostrar.
   */
  interaction: ManagedInteraction;
  /** El nivel con clic no pudo completar. Es el motivo, para elegir el mensaje. */
  failure: string | null;
};

type ControllerDeps = {
  loadConfig: () => Promise<BotCheckConfig>;
  loadScript: () => Promise<TurnstileApi>;
  report: (event: BotEvent) => void;
};

type Widget = {
  el: HTMLElement | null;
  api: TurnstileApi | null;
  widgetId: string | null;
  /** Sube cada vez que se pide o se saca un widget: descarta callbacks y cargas viejas. */
  gen: number;
  /** Se pidió el render y todavía está bajando el script. */
  pending: boolean;
  renderedAt: number | null;
  token: string | null;
  error: string | null;
};

type ManagedWidget = Widget & {
  /** Un `getProof()` lo necesita: recién ahí se monta. */
  wanted: boolean;
  errors: number;
  timeouts: number;
  interaction: ManagedInteraction;
};

/**
 * Re-render del widget después de usar un token. Diferido un instante para que, si el formulario
 * cambia de paso (teléfono → código), el widget se monte una sola vez en el contenedor nuevo y no
 * dos (una en el que se va y otra en el que llega): cada render es un desafío más en Cloudflare.
 */
export const RERENDER_DELAY_MS = 100;

/** Errores del nivel con clic antes de rendirse (Cloudflare reintenta solo entre medio). */
export const MANAGED_MAX_ERRORS = 2;
/** Vencimientos de la casilla sin tocar antes de rendirse. */
export const MANAGED_MAX_TIMEOUTS = 2;
/**
 * Si el nivel con clic no da token, ni error, ni pide la casilla en este tiempo, algo quedó
 * colgado (un iframe que nunca cargó). Mejor un mensaje con "Reintentar" que un botón girando.
 */
export const MANAGED_SILENT_TIMEOUT_MS = 30_000;
/**
 * El contenedor del nivel con clic lo monta el slot recién después de escalar (antes no ocupa
 * lugar en el formulario). Si en este tiempo no apareció, el formulario no tiene slot: falla con
 * motivo en vez de esperar para siempre.
 */
export const MANAGED_SLOT_WAIT_MS = 3000;

const emptyWidget = (): Widget => ({
  el: null,
  api: null,
  widgetId: null,
  gen: 0,
  pending: false,
  renderedAt: null,
  token: null,
  error: null,
});

const INITIAL_SNAPSHOT: TurnstileSnapshot = {
  enabled: false,
  tier: "invisible",
  verifying: false,
  interaction: null,
  failure: null,
};

export class TurnstileController {
  readonly surface: AuthSurface;
  private readonly deps: ControllerDeps;

  private config: BotCheckConfig | null = null;
  private configPromise: Promise<BotCheckConfig> | null = null;
  private active = false;

  private tier: TurnstileTier = "invisible";
  private attemptId = newAttemptId();
  private invisible: Widget = emptyWidget();
  private managed: ManagedWidget = {
    ...emptyWidget(),
    wanted: false,
    errors: 0,
    timeouts: 0,
    interaction: null,
  };
  /** El script no cargó o el navegador no está soportado. */
  private unavailable: string | null = null;
  private failure: string | null = null;
  /** La sitekey del nivel con clic que mandó la API en un step-up (por si la config no la tenía). */
  private managedSiteKeyOverride: string | null = null;
  /** Por qué se escaló (viaja con los pedidos del nivel con clic: explica la fila en el panel). */
  private escalationReason: string | null = null;

  private waitStartedAt: number | null = null;
  private waiters = new Set<() => void>();
  private listeners = new Set<() => void>();
  private snapshot: TurnstileSnapshot = INITIAL_SNAPSHOT;
  private rerenderTimer: ReturnType<typeof setTimeout> | null = null;
  private watchdog: ReturnType<typeof setTimeout> | null = null;
  private reported = new Set<string>();
  /** `getProof()` en curso: hay un pedido de código en marcha. */
  private proofsInFlight = 0;
  /**
   * Avisos del widget de antes de que la persona pidiera un código (ver `reportWhenRequested`).
   * Salen con el próximo pedido y, si no hay pedido, nunca.
   */
  private deferredReports: Array<{ key: string; event: Omit<BotEvent, "surface"> }> = [];

  constructor(surface: AuthSurface, deps: Partial<ControllerDeps> = {}) {
    this.surface = surface;
    this.deps = {
      loadConfig: deps.loadConfig ?? loadBotCheckConfig,
      loadScript: deps.loadScript ?? ensureTurnstileScript,
      report: deps.report ?? reportBotEvent,
    };
  }

  /* ───────────── ciclo de vida (lo maneja `useTurnstile`) ───────────── */

  activate = () => {
    if (this.active) return;
    this.active = true;
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.onPageHide);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
    void this.ensureConfig().then(() => this.sync());
  };

  deactivate = () => {
    if (!this.active) return;
    // Desmontar mientras se esperaba es irse de la comprobación (una navegación de la SPA).
    this.reportAbandoned();
    // Los avisos guardados son de alguien que no pidió ningún código: no salen (y si el
    // formulario vuelve, se pueden volver a avisar).
    for (const { key } of this.deferredReports) this.reported.delete(key);
    this.deferredReports = [];
    this.active = false;
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.onPageHide);
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    this.clearTimer("rerender");
    this.clearTimer("watchdog");
    this.removeWidget(this.invisible);
    this.removeWidget(this.managed);
    this.invisible.token = null;
    this.managed.token = null;
    this.managed.wanted = false;
    this.managed.interaction = null;
    this.notify();
  };

  /* ───────────── contenedores (refs de `TurnstileSlot`) ───────────── */

  /** Ref del contenedor del widget invisible. Devuelve la limpieza (refs con cleanup de React 19). */
  attachInvisible = (el: HTMLElement | null) => this.attach(this.invisible, el);

  /** Ref del contenedor del nivel con clic. */
  attachManaged = (el: HTMLElement | null) => this.attach(this.managed, el);

  private attach(widget: Widget, el: HTMLElement | null) {
    if (!el) return undefined;
    if (widget.el && widget.el !== el) this.releaseContainer(widget, widget.el);
    widget.el = el;
    this.sync();
    return () => this.releaseContainer(widget, el);
  }

  private releaseContainer(widget: Widget, el: HTMLElement) {
    if (widget.el !== el) return;
    this.removeWidget(widget);
    // El token de un widget que ya no está queda sin quién avise si vence: se descarta y el
    // contenedor nuevo pide otro.
    widget.token = null;
    widget.el = null;
    this.notify();
  }

  /* ───────────── suscripción (useSyncExternalStore) ───────────── */

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.snapshot;

  getServerSnapshot = () => INITIAL_SNAPSHOT;

  /** El id del intento en curso (el `cData` del widget). Cambia con cada `reset()`. */
  getAttemptId = () => this.attemptId;

  /* ───────────── API para los formularios ───────────── */

  /**
   * La constancia anti-bot para mandar con el pedido de código. Espera el token del nivel en
   * curso y, si el invisible no lo da, escala al nivel con clic y espera ese (con el clic de la
   * persona, si Cloudflare lo pide). Nunca tira y nunca cuelga: si no hay token, dice por qué.
   */
  getProof = async (): Promise<TurnstileProof> => {
    const startedAt = Date.now();
    const waited = () => Math.max(0, Date.now() - startedAt);
    // Sin formulario no hay pedido: `failed` corta el envío (si no, salía un send-otp sin token
    // de alguien que ya se fue, y el panel lo contaba como un pedido rechazado).
    const unmounted = (): TurnstileProof => ({
      tier: this.tier,
      clientError: "unmounted",
      waitedMs: waited(),
      failed: true,
    });
    const config = await this.ensureConfig();

    if (!this.active) return unmounted();
    // Apagado: la API no verifica nada, así que no hay nada que esperar.
    if (!this.enabled) return { tier: this.tier, waitedMs: waited() };

    this.proofsInFlight += 1;
    // La persona pidió un código: recién ahora salen los avisos del widget que se guardaron.
    this.flushDeferredReports();
    try {
      if (this.tier === "invisible") {
        const invisible = this.invisible;
        if (!invisible.token && !invisible.error && !this.unavailable && this.invisibleKey()) {
          this.sync();
          this.beginWait();
          const base = invisible.renderedAt ?? startedAt;
          await this.waitFor(
            () =>
              !this.active ||
              this.tier !== "invisible" ||
              Boolean(invisible.token || invisible.error || this.unavailable),
            base + config.graceMs - Date.now(),
          );
        }
        if (!this.active) return unmounted();

        if (this.tier === "invisible") {
          if (invisible.token) {
            return { token: invisible.token, tier: "invisible", waitedMs: waited() };
          }
          const reason =
            this.unavailable ?? invisible.error ?? (this.invisibleKey() ? "timeout" : "no_sitekey");
          // Sin nivel con clic configurado no hay a dónde escalar: decide la API (que contesta
          // como siempre, con el aviso y la cuenta regresiva).
          if (!this.managedKey()) {
            return { tier: "invisible", clientError: reason, waitedMs: waited() };
          }
          this.escalate(reason);
        }
      }
      return await this.managedProof(waited);
    } finally {
      this.proofsInFlight -= 1;
      this.endWait();
    }
  };

  /**
   * Pasa al nivel con clic: por el propio invisible (`timeout`, código de error, script caído) o
   * porque lo pidió la API (`server_step_up`, con la sitekey en el body). Una sola vez por
   * formulario; después todo sigue en este nivel.
   */
  escalate = (reason: string, managedSiteKey?: string | null) => {
    // Formulario desmontado (la persona se fue): no hay nada que escalar ni que avisar.
    if (!this.active) return;
    const key = managedSiteKey?.trim();
    if (key) this.managedSiteKeyOverride = key;
    if (this.tier === "managed") {
      this.notify();
      return;
    }
    const waitMs = this.invisible.renderedAt ? Date.now() - this.invisible.renderedAt : undefined;
    this.tier = "managed";
    this.escalationReason = reason;
    // Sacarlo corta sus reintentos automáticos (y el ruido en las métricas de Cloudflare).
    this.removeWidget(this.invisible);
    this.invisible.token = null;
    this.invisible.error = null;
    this.report({ event: "escalated", tier: "invisible", clientError: reason, waitMs });
    this.notify();
  };

  /**
   * Después de usar un token (salga bien o mal el pedido): los tokens son de un solo uso. Arranca
   * un intento nuevo (otro `attemptId`) y, en el nivel invisible, pide un token fresco para que el
   * próximo envío (p. ej. "Reenviar código") no espere. El mensaje de falla, si hay, queda a la
   * vista hasta el próximo intento.
   */
  reset = () => {
    this.attemptId = newAttemptId();
    this.dropTokens();
    this.scheduleRerender();
  };

  /** Token nuevo para el MISMO intento (la API dijo que el anterior venció). */
  refresh = () => {
    this.dropTokens();
    this.sync();
  };

  /* ───────────── nivel con clic ───────────── */

  private async managedProof(waited: () => number): Promise<TurnstileProof> {
    const managed = this.managed;
    // Pedir la constancia otra vez después de una falla ES el reintento.
    if (this.failure) {
      this.failure = null;
      managed.errors = 0;
      managed.timeouts = 0;
    }
    if (!this.managedKey()) {
      this.fail("no_managed_sitekey");
    } else if (!managed.token) {
      managed.wanted = true;
      // Si el contenedor ya está, se monta acá; si no, se monta apenas el slot lo enganche.
      this.sync();
      this.beginWait();
      const noSlot = setTimeout(() => {
        if (this.active && !managed.el && !managed.token) this.fail("no_slot");
      }, MANAGED_SLOT_WAIT_MS);
      await this.waitFor(() => !this.active || Boolean(managed.token) || this.failure !== null);
      clearTimeout(noSlot);
    }

    if (managed.token) {
      return {
        token: managed.token,
        tier: "managed",
        waitedMs: waited(),
        ...(this.escalationReason ? { clientError: this.escalationReason } : {}),
      };
    }
    return {
      tier: "managed",
      clientError: this.active ? (this.failure ?? "managed_failed") : "unmounted",
      waitedMs: waited(),
      failed: true,
    };
  }

  private fail(reason: string) {
    this.failure = reason;
    this.managed.wanted = false;
    this.managed.interaction = null;
    this.clearTimer("watchdog");
    this.removeWidget(this.managed);
    this.notify();
  }

  /* ───────────── render de widgets ───────────── */

  private get enabled() {
    return Boolean(this.config?.enabled) || this.managedSiteKeyOverride !== null;
  }

  private invisibleKey() {
    return this.config?.invisibleSiteKey ?? null;
  }

  private managedKey() {
    return this.managedSiteKeyOverride ?? this.config?.managedSiteKey ?? null;
  }

  private sync() {
    if (!this.active || !this.enabled) return;
    const invisible = this.invisible;
    if (
      this.tier === "invisible" &&
      invisible.el &&
      !invisible.widgetId &&
      !invisible.pending &&
      !this.unavailable &&
      this.invisibleKey()
    ) {
      void this.render(invisible, "invisible");
    }
    const managed = this.managed;
    if (
      this.tier === "managed" &&
      managed.wanted &&
      managed.el &&
      !managed.widgetId &&
      !managed.pending &&
      !this.failure &&
      this.managedKey()
    ) {
      void this.render(managed, "managed");
    }
  }

  private async render(widget: Widget, tier: TurnstileTier) {
    const gen = ++widget.gen;
    widget.pending = true;
    widget.renderedAt = Date.now();
    this.notify();

    let api: TurnstileApi;
    try {
      api = await this.deps.loadScript();
    } catch (error) {
      if (gen !== widget.gen) return;
      widget.pending = false;
      this.markUnavailable(
        tier,
        error instanceof TurnstileScriptError ? error.reason : "script_error",
      );
      return;
    }

    if (gen !== widget.gen) return;
    widget.pending = false;
    const el = widget.el;
    const stillWanted = tier === "invisible" ? this.tier === "invisible" : this.managed.wanted;
    if (!this.active || !el || !stillWanted) return;

    // El script cargó: si antes había fallado, ya no corresponde decir que no está.
    this.unavailable = null;
    el.innerHTML = "";
    try {
      const options =
        tier === "invisible" ? this.invisibleOptions(gen) : this.managedOptions(gen, el);
      const widgetId = api.render(el, options);
      if (!widgetId) throw new Error("render_error");
      widget.api = api;
      widget.widgetId = widgetId;
    } catch {
      this.markUnavailable(tier, "render_error");
      return;
    }
    if (tier === "managed") this.armWatchdog(gen);
    this.notify();
  }

  private removeWidget(widget: Widget) {
    widget.gen += 1;
    widget.pending = false;
    if (widget.widgetId) {
      try {
        (widget.api ?? (typeof window !== "undefined" ? window.turnstile : undefined))?.remove(
          widget.widgetId,
        );
      } catch {
        // Ya no estaba (Cloudflare lo limpió solo): nada que hacer.
      }
    }
    widget.widgetId = null;
  }

  private dropTokens() {
    this.clearTimer("watchdog");
    this.removeWidget(this.invisible);
    this.invisible.token = null;
    this.invisible.error = null;
    this.invisible.renderedAt = null;
    this.removeWidget(this.managed);
    this.managed.token = null;
    this.managed.error = null;
    this.managed.renderedAt = null;
    this.managed.wanted = false;
    this.managed.errors = 0;
    this.managed.timeouts = 0;
    this.managed.interaction = null;
    this.unavailable = null;
    this.notify();
  }

  /** Corre `fn` sólo si el callback es del widget vigente (no de uno ya sacado o reemplazado). */
  private current(widget: Widget, gen: number, fn: () => void) {
    if (gen === widget.gen && this.active) fn();
  }

  private invisibleOptions(gen: number): TurnstileRenderOptions {
    const widget = this.invisible;
    return {
      sitekey: this.invisibleKey(),
      // Una acción por pantalla: la API rechaza un token de otra pantalla, y Cloudflare separa
      // las métricas por acción.
      action: this.surface,
      cData: this.attemptId,
      retry: "auto",
      // El default (8 s) es más de lo que se tarda en tipear un teléfono.
      "retry-interval": 2500,
      "refresh-expired": "auto",
      language: "es",
      // La SPA manda el token ella misma: el input oculto de Cloudflare sobra.
      "response-field": false,
      // Sin `appearance`: en un widget invisible no hace nada (sólo aplica a los visibles).
      callback: (token: string) =>
        this.current(widget, gen, () => this.onToken(widget, "invisible", token)),
      "error-callback": (code: unknown) => {
        this.current(widget, gen, () => this.onError(widget, "invisible", code));
        // true = "manejado": sin esto Turnstile ensucia la consola (o tira, si no hay callback).
        return true;
      },
      "expired-callback": () =>
        this.current(widget, gen, () => {
          // El token venció (300 s) y Turnstile ya está pidiendo otro (`refresh-expired: auto`):
          // el plazo vuelve a contar desde acá. Si no, quien envía justo en ese segundo escalaría
          // al nivel con clic sin motivo.
          widget.token = null;
          widget.renderedAt = Date.now();
          this.notify();
        }),
      "unsupported-callback": () =>
        this.current(widget, gen, () => this.markUnavailable("invisible", "unsupported")),
    };
  }

  private managedOptions(gen: number, el: HTMLElement): TurnstileRenderOptions {
    const widget = this.managed;
    return {
      sitekey: this.managedKey(),
      action: this.surface,
      cData: this.attemptId,
      // Sólo se ve si Cloudflare necesita el clic; si lo resuelve solo, no aparece nada.
      appearance: "interaction-only",
      // `flexible` ocupa el ancho (mínimo 300 px); en un celular angosto no entra.
      size: el.clientWidth >= 300 ? "flexible" : "compact",
      theme: "light",
      language: "es",
      "refresh-expired": "auto",
      "refresh-timeout": "auto",
      // Que una persona real a la que le falla pueda avisarle a Cloudflare.
      "feedback-enabled": true,
      "response-field": false,
      callback: (token: string) =>
        this.current(widget, gen, () => this.onToken(widget, "managed", token)),
      "error-callback": (code: unknown) => {
        this.current(widget, gen, () => this.onError(widget, "managed", code));
        return true;
      },
      "expired-callback": () =>
        this.current(widget, gen, () => {
          widget.token = null;
          this.notify();
        }),
      "timeout-callback": () =>
        this.current(widget, gen, () => {
          // La casilla venció sin que nadie la tocara; Cloudflare la vuelve a mostrar sola.
          this.managed.timeouts += 1;
          if (this.managed.timeouts >= MANAGED_MAX_TIMEOUTS) this.fail("interaction_timeout");
          else this.notify();
        }),
      "before-interactive-callback": () =>
        this.current(widget, gen, () => {
          // La persona puede tardar en tocarla: mientras la casilla está a la vista no hay
          // vigilancia de cuelgue.
          this.clearTimer("watchdog");
          this.managed.interaction = "prompt";
          this.notify();
        }),
      "after-interactive-callback": () =>
        this.current(widget, gen, () => {
          this.managed.interaction = "done";
          this.armWatchdog(gen);
          this.notify();
        }),
      "unsupported-callback": () =>
        this.current(widget, gen, () => this.markUnavailable("managed", "unsupported")),
    };
  }

  private onToken(widget: Widget, tier: TurnstileTier, token: string) {
    if (!token) return;
    widget.token = token;
    widget.error = null;
    if (tier === "managed") {
      this.clearTimer("watchdog");
      this.reportOnce(`solved:${this.attemptId}`, {
        event: "managed_solved",
        tier: "managed",
        waitMs: widget.renderedAt ? Date.now() - widget.renderedAt : undefined,
      });
    }
    this.notify();
  }

  private onError(widget: Widget, tier: TurnstileTier, code: unknown) {
    const reason = String(code ?? "").trim() || "unknown";
    widget.token = null;
    widget.error = reason;
    // Con `retry: auto` el mismo problema puede llamar varias veces: un beacon por código.
    this.reportWhenRequested(`error:${this.attemptId}:${tier}:${reason}`, {
      event: "widget_error",
      tier,
      clientError: reason,
    });
    if (tier === "managed") {
      this.managed.errors += 1;
      if (this.managed.errors >= MANAGED_MAX_ERRORS) {
        this.fail(reason);
        return;
      }
    }
    this.notify();
  }

  private markUnavailable(tier: TurnstileTier, reason: string) {
    this.unavailable = reason;
    this.reportWhenRequested(`unavailable:${reason}`, {
      event: "script_unavailable",
      tier,
      clientError: reason,
    });
    if (tier === "managed") this.fail(reason);
    else this.notify();
  }

  private armWatchdog(gen: number) {
    this.clearTimer("watchdog");
    this.watchdog = setTimeout(() => {
      this.watchdog = null;
      if (gen !== this.managed.gen || this.managed.token || this.managed.interaction === "prompt") {
        return;
      }
      this.reportOnce(`error:${this.attemptId}:managed:managed_timeout`, {
        event: "widget_error",
        tier: "managed",
        clientError: "managed_timeout",
      });
      this.fail("managed_timeout");
    }, MANAGED_SILENT_TIMEOUT_MS);
  }

  private scheduleRerender() {
    this.clearTimer("rerender");
    this.rerenderTimer = setTimeout(() => {
      this.rerenderTimer = null;
      this.sync();
    }, RERENDER_DELAY_MS);
  }

  private clearTimer(which: "rerender" | "watchdog") {
    if (which === "rerender" && this.rerenderTimer) {
      clearTimeout(this.rerenderTimer);
      this.rerenderTimer = null;
    }
    if (which === "watchdog" && this.watchdog) {
      clearTimeout(this.watchdog);
      this.watchdog = null;
    }
  }

  /* ───────────── config, esperas y avisos ───────────── */

  private ensureConfig(): Promise<BotCheckConfig> {
    if (this.config) return Promise.resolve(this.config);
    if (!this.configPromise) {
      this.configPromise = this.deps
        .loadConfig()
        .catch(() => fallbackBotCheckConfig())
        .then((config) => {
          this.config = config;
          this.notify();
          this.sync();
          return config;
        });
    }
    return this.configPromise;
  }

  private waitFor(done: () => boolean, timeoutMs?: number): Promise<void> {
    if (done()) return Promise.resolve();
    return new Promise<void>((resolve) => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const finish = () => {
        if (timer) clearTimeout(timer);
        this.waiters.delete(check);
        resolve();
      };
      const check = () => {
        if (done()) finish();
      };
      this.waiters.add(check);
      if (timeoutMs !== undefined) timer = setTimeout(finish, Math.max(0, timeoutMs));
    });
  }

  private beginWait() {
    if (this.waitStartedAt === null) this.waitStartedAt = Date.now();
    this.notify();
  }

  private endWait() {
    this.waitStartedAt = null;
    this.notify();
  }

  private notify() {
    for (const check of Array.from(this.waiters)) check();
    const next: TurnstileSnapshot = {
      enabled: this.enabled,
      tier: this.tier,
      verifying: this.waitStartedAt !== null,
      interaction: this.tier === "managed" ? this.managed.interaction : null,
      failure: this.failure,
    };
    const prev = this.snapshot;
    if (
      prev.enabled === next.enabled &&
      prev.tier === next.tier &&
      prev.verifying === next.verifying &&
      prev.interaction === next.interaction &&
      prev.failure === next.failure
    ) {
      return;
    }
    this.snapshot = next;
    for (const listener of Array.from(this.listeners)) listener();
  }

  private report(event: Omit<BotEvent, "attemptId" | "surface">) {
    this.deps.report({ ...event, attemptId: this.attemptId, surface: this.surface });
  }

  private reportOnce(key: string, event: Omit<BotEvent, "attemptId" | "surface">) {
    if (this.reported.has(key)) return;
    this.reported.add(key);
    this.report(event);
  }

  /**
   * Un aviso del widget (error, script que no cargó), que puede pasar apenas se abre el
   * formulario. Con un pedido de código en curso sale ya; si no, se guarda (con el intento de ese
   * momento, que es el del próximo pedido) y sale cuando la persona pide el código. Si nunca lo
   * pide, no sale: abrir un formulario y irse no deja registro, como dice la política de
   * privacidad ("cada vez que pedís un código…"). Uno por clave, igual que `reportOnce`.
   */
  private reportWhenRequested(key: string, event: Omit<BotEvent, "attemptId" | "surface">) {
    if (this.proofsInFlight > 0) {
      this.reportOnce(key, event);
      return;
    }
    if (this.reported.has(key)) return;
    this.reported.add(key);
    this.deferredReports.push({ key, event: { ...event, attemptId: this.attemptId } });
  }

  private flushDeferredReports() {
    const pending = this.deferredReports;
    this.deferredReports = [];
    for (const { event } of pending) this.deps.report({ ...event, surface: this.surface });
  }

  /**
   * La persona se fue (cerró la pestaña, cambió de app, navegó) mientras se esperaba la
   * comprobación o con la casilla a la vista sin tocar. Un aviso por intento.
   */
  private reportAbandoned() {
    const prompting =
      this.tier === "managed" && this.managed.interaction === "prompt" && !this.managed.token;
    if (this.waitStartedAt === null && !prompting) return;
    this.reportOnce(`abandoned:${this.attemptId}`, {
      event: "abandoned",
      tier: this.tier,
      clientError: prompting ? "interactive" : "waiting",
      waitMs: this.waitStartedAt !== null ? Date.now() - this.waitStartedAt : undefined,
    });
  }

  private onPageHide = () => this.reportAbandoned();

  private onVisibilityChange = () => {
    if (document.visibilityState === "hidden") this.reportAbandoned();
  };
}
